import React, { useRef, useState } from "react";

import { useTranslation } from "react-i18next";
import { FrontendUserLicense } from "node-library/dist/portal";
import {
	LicenseDesignation,
	LicenseOption,
	LicenseType,
	Severity,
} from "node-library/dist/constants";
import { useSnackbar } from "../../common-components/toast/showToastMessage";
import { NeutralColor, PrimaryColor } from "node-library/dist/colors";
import { Card } from "@mui/material";
import {
	DeleteIcon,
	EditIcon,
	LicenseIcon,
} from "../../common-components/svgIcons";
import {
	BlueButton,
	KebabDropdownButton,
} from "../../common-components/button";
import AddIcon from "@mui/icons-material/Add";
import AddOrEditLicenseModal from "./components/AddOrEditLicenseModal";

interface Props {
	userLicenses: FrontendUserLicense[];
	setUserLicenses: React.Dispatch<React.SetStateAction<FrontendUserLicense[]>>;
	setUnsavedChanges: React.Dispatch<React.SetStateAction<boolean>>;
}

export default function UserLicenseInfoSection(props: Props) {
	const { t: translation } = useTranslation();

	//license info
	const { userLicenses, setUserLicenses, setUnsavedChanges } = props;

	const [newLicenseModalOpen, setNewLicenseModalOpen] =
		useState<boolean>(false);

	const [updatingLicense, setUpdatingLicense] = useState<FrontendUserLicense>();

	const [proceedAction, setProceedAction] =
		useState<(license: FrontendUserLicense) => void>();

	const addLicense = (newLicense: FrontendUserLicense) => {
		setUserLicenses((prevLicenses) => [
			...prevLicenses, // Spread the previous licenses
			newLicense, // Add the new license at the end
		]);
		setUnsavedChanges(true);
		setNewLicenseModalOpen(false);
	};

	const updateLicense = (updatedLicense: FrontendUserLicense) => {
		setUserLicenses((prevLicenses) =>
			prevLicenses.map((license) =>
				license.licenseId === updatedLicense.licenseId
					? updatedLicense // Merge the updated data with the current license
					: license
			)
		);
		setUnsavedChanges(true);
		setUpdatingLicense(undefined);
		setNewLicenseModalOpen(false);
	};

	// Function to delete an item by licenseId
	const deleteLicense = (licenseId: string) => {
		setUserLicenses((prevLicenses) =>
			prevLicenses.filter((license) => license.licenseId !== licenseId)
		);
		setUnsavedChanges(true);
		setUpdatingLicense(undefined);
	};

	const licenseOptionCards: Array<React.ReactNode> = userLicenses.map(
		(license, index) => {
			return (
				<Card
					className={`card-border-radius-10p card-medium-padding`}
					tabIndex={0}
					key={license.licenseId}
					id={`profile-license-option-card-${index}`}
					sx={{ backgroundColor: "#F3F3F5", height: "160px" }}
				>
					<div
						style={{
							display: "flex",
							flexDirection: "column",
							justifyContent: "space-between",
							height: "100%",
						}}
					>
						<div>
							<div
								style={{
									display: "flex",
									justifyContent: "space-between",
									gap: "10px",
								}}
							>
								<div
									style={{
										display: "flex",
										flexDirection: "column",
										gap: "5px",
										maxWidth: "80%",
									}}
								>
									<div
										className="typography-caption"
										style={{ color: NeutralColor.NEUTRAL_GREY_500 }}
									>
										{translation("LicenseNo")}
									</div>
									<div className="typography-heading-03 overflow-ellipsis-text">
										{license.licenseNumber}
									</div>
								</div>
								<KebabDropdownButton
									menuOptions={[
										{
											key: `edit-license-${license.licenseId}`,
											onClick: () => {
												setUpdatingLicense(license);
												setProceedAction(() => updateLicense);
												setNewLicenseModalOpen(true);
											},
											children: (
												<div className="icon-dropdown-menu-item">
													<EditIcon
														strokeColor={NeutralColor.NEUTRAL_GREY_900}
													/>
													{translation("Edit")}
												</div>
											),
										},
										{
											key: `delete-license-${license.licenseId}`,
											onClick: () => {
												deleteLicense(license.licenseId);
											},
											children: (
												<div className="icon-dropdown-menu-item">
													<DeleteIcon />
													{translation("Delete")}
												</div>
											),
										},
									]}
									id={`license-more-action-menu-${license.licenseId}`}
									onClick={(e) => {}}
								></KebabDropdownButton>
							</div>
							{license.nickName && (
								<div
									className="typography-caption overflow-ellipsis-text"
									style={{ color: NeutralColor.NEUTRAL_GREY_500 }}
								>
									{`Nickname - ${license.nickName}`}
								</div>
							)}
						</div>
						<div className="typography-caption profile-license-designation-type-row">
							<div className="overflow-ellipsis-text">
								<div
									className="typography-caption"
									style={{ color: NeutralColor.NEUTRAL_GREY_500 }}
								>
									{translation("LicenseType")}
								</div>
								<div className="typography-body-02">
									{translation(license.licenseType)}
								</div>
							</div>

							<div className="overflow-ellipsis-text">
								<div
									className="typography-caption"
									style={{ color: NeutralColor.NEUTRAL_GREY_500 }}
								>
									{translation("Designation")}
								</div>
								<div className="typography-body-02">
									{translation(license.licenseDesignation)}
								</div>
							</div>
						</div>
					</div>
				</Card>
			);
		}
	);

	const newLicenseCard: React.ReactNode = (
		<Card
			className={`card-border-radius-10p card-medium-padding`}
			tabIndex={0}
			sx={{
				border: `1px solid ${PrimaryColor.PRIMARY_800}`,
				height: "160px",
				color: PrimaryColor.PRIMARY_600,
				cursor: "pointer",
				transition: "all 0.3s ease", // Smooth transition
				"&:hover": {
					backgroundColor: PrimaryColor.PRIMARY_50, // Color on hover
				},
				"&:active": {
					backgroundColor: PrimaryColor.PRIMARY_100, // Color when pressed
				},
			}}
			onClick={() => {
				setUpdatingLicense(undefined);
				setProceedAction(() => addLicense);
				setNewLicenseModalOpen(true);
			}}
		>
			<div
				style={{
					height: "100%",
					display: "flex",
					justifyContent: "center",
					alignItems: "center",
				}}
				className="typography-body-01"
			>
				<AddIcon></AddIcon>
				{translation("NewLicense")}
			</div>
		</Card>
	);

	const newLicenseModal: React.ReactNode = (
		<AddOrEditLicenseModal
			open={newLicenseModalOpen}
			setOpen={setNewLicenseModalOpen}
			license={updatingLicense}
			proceedAction={proceedAction as (license: FrontendUserLicense) => void}
		></AddOrEditLicenseModal>
	);

	const licenseInformation: React.ReactNode = (
		<div>
			<div className="profile-information-container">
				<div className="profile-icon-header-row typography-heading-03">
					<LicenseIcon></LicenseIcon>
					{translation("LicenseInformation")}
				</div>
				<div
					className="typography-caption"
					style={{ color: NeutralColor.NEUTRAL_GREY_500 }}
				>
					{translation("LicenseInformationDescription")}
				</div>
			</div>

			<div className="profile-license-card-grid">
				{licenseOptionCards} {newLicenseCard}
			</div>
			{newLicenseModal}
		</div>
	);

	return licenseInformation;
}
